import { JSX } from 'react';
import {
  CURRENCY_LOCALE_MAP,
  CURRENCY_SYMBOL_MAP,
  DEFAULT_CURRENCY_CODE,
} from '@neogeniux/api-contracts';
import { useAuthStore } from '../store/auth.store';

// Helper function to get locale from currency
const getLocaleFromCurrency = (currency?: string): string => {
  if (!currency) return CURRENCY_LOCALE_MAP.DOP;
  return CURRENCY_LOCALE_MAP[currency.toUpperCase()] ?? CURRENCY_LOCALE_MAP.DOP;
};

// Helper function to get current user's currency
const getCurrentCurrency = (): string => {
  const authState = useAuthStore.getState();
  return authState.user?.business?.currency || DEFAULT_CURRENCY_CODE;
};

/**
 * Returns the display symbol for the given (or current business)
 * currency. Use this for input prefixes / suffixes so the symbol
 * tracks the business's `currency` setting instead of being
 * hardcoded to a specific market.
 */
export const getCurrencySymbol = (currency?: string): string => {
  const actualCurrency = (currency || getCurrentCurrency()).toUpperCase();
  if (CURRENCY_SYMBOL_MAP[actualCurrency]) {
    return CURRENCY_SYMBOL_MAP[actualCurrency];
  }
  // Fallback: let Intl emit the symbol and slice off the digits.
  const formatted = (0).toLocaleString(getLocaleFromCurrency(actualCurrency), {
    style: 'currency',
    currency: actualCurrency,
  });
  return formatted.match(/^[^\d.,\s]+/)?.[0] ?? actualCurrency;
};

export const DATE_FORMATS = {
  SHORT_ES: 'dd/MM/yyyy',
  SHORT_EN: 'MM/dd/yyyy',
  MEDIUM: 'MM MMM yyyy',
  LONG: 'dd MMMM yyyy',
};

export const FORMATS = {
  DATE: DATE_FORMATS,
  TIME: 'HH:mm',
  DATETIME: 'dd/MM/yyyy HH:mm',
} as const;

export const formatToDate = (date: Date, format?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  switch (format) {
    case FORMATS.DATE.LONG:
    case FORMATS.DATE.MEDIUM:
    case FORMATS.DATE.SHORT_EN:
    case FORMATS.DATE.SHORT_ES:
      options.month = '2-digit';
      options.day = '2-digit';
      break;
    case FORMATS.TIME:
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.hour12 = true;
      return date.toLocaleTimeString('es-DO', options);
    case FORMATS.DATETIME:
      options.month = '2-digit';
      options.day = '2-digit';
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.hour12 = true;
      return date.toLocaleString('es-DO', options);
    default:
      options.month = 'long';
      options.day = 'numeric';
      options.year = 'numeric';
      break;
  }

  return date.toLocaleDateString('es-DO', options);
};

/**
 * Format a Date to a local YYYY-MM-DD string without timezone shift.
 * Unlike `date.toISOString().split('T')[0]` which converts to UTC first
 * (causing the date to shift +1 day in negative UTC offsets like America/Santo_Domingo),
 * this uses the local year/month/day components.
 *
 * @param date - The date to format (defaults to now)
 * @returns Date string in YYYY-MM-DD format using local time
 */
export const toLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatToCurrency = (
  value: number | null | undefined,
  currency?: string,
  classNames?: {
    container?: string;
    currencySymbol?: string;
    amount?: string;
  }
): JSX.Element => {
  const actualCurrency = currency || getCurrentCurrency();
  const locale = getLocaleFromCurrency(actualCurrency);
  // Backend can omit numeric fields when a business has no rows yet
  // (e.g. fresh tenant on dashboard). Treat null/undefined/NaN as 0
  // so the component renders instead of crashing the whole page.
  const safeValue =
    typeof value === 'number' && Number.isFinite(value) ? value : 0;

  const formatted = safeValue.toLocaleString(locale, {
    style: 'currency',
    currency: actualCurrency,
  });

  // Preserve sign — the greedy `^[^\d.,]+` strip below eats the leading
  // "-" alongside the "RD$" and the operator loses the minus on a
  // negative tile (Ventas Hoy < 0 when credit notes outstrip payments).
  const isNegative = safeValue < 0;

  // Use custom symbol if available, otherwise extract from formatted string
  const currencySymbol =
    CURRENCY_SYMBOL_MAP[actualCurrency] ||
    formatted.match(/^-?[^\d.,-]+/)?.[0]?.replace(/^-/, '') ||
    '';
  const amount = formatted.replace(/^-?[^\d.,-]+/, '');

  return (
    <span
      className={
        classNames?.container ||
        'inline-flex items-center space-x-1 whitespace-nowrap'
      }
    >
      <span className={classNames?.currencySymbol || 'text-xs'}>
        {currencySymbol}
      </span>
      <span className={classNames?.amount || 'text-sm'}>
        {isNegative ? '-' : ''}
        {amount}
      </span>
    </span>
  );
};

export const formatToCurrencyString = (
  value: number | null | undefined,
  currency?: string,
  customSymbol?: string
): string => {
  const actualCurrency = currency || getCurrentCurrency();
  const locale = getLocaleFromCurrency(actualCurrency);
  const safeValue =
    typeof value === 'number' && Number.isFinite(value) ? value : 0;

  const formatted = safeValue.toLocaleString(locale, {
    style: 'currency',
    currency: actualCurrency,
  });

  // Preserve sign — see formatToCurrency() for the same fix.
  const isNegative = safeValue < 0;

  // If custom symbol is provided, use it; otherwise use mapped symbol or extract from formatted string
  const currencySymbol =
    customSymbol ||
    CURRENCY_SYMBOL_MAP[actualCurrency] ||
    formatted.match(/^-?[^\d.,-]+/)?.[0]?.replace(/^-/, '') ||
    '';
  const amount = formatted.replace(/^-?[^\d.,-]+/, '');

  // Return currency symbol with a space before the amount
  return `${currencySymbol} ${isNegative ? '-' : ''}${amount}`;
};

export const formatToMoney = (
  value: number | string | null | undefined,
  currency?: string
): string => {
  const actualCurrency = currency || getCurrentCurrency();
  const locale = getLocaleFromCurrency(actualCurrency);

  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  const numericValue =
    typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : 0;

  return numericValue.toLocaleString(locale, {
    style: 'currency',
    currency: actualCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
